"use client";

import { memo, useMemo } from "react";
import { useResponsiveLimit } from "@/hooks/shared/use-responsive-limit";
import type { ShowFacepileData } from "@/server/api/routers/show/discovery";
import { ShowDiscoveryCard } from "../cards/show-discovery-card";
import { ShowDateDivider } from "../shared/show-date-divider";

interface Venue {
	id: string;
	name: string;
	slug: string;
	address?: string;
	city: string;
	state?: string;
	country: string;
	capacity?: number;
	venue_type?: string;
}

interface Show {
	id: string;
	title: string;
	artist_name: string;
	date: string;
	time_start?: string;
	time_end?: string;
	location: string;
	age_limit?: string;
	poster_url?: string;
	slug: string;
	created_at: string;
	updated_at: string;
	artist_id?: string;
	venue_id: string;
	venue: Venue;
	website_url?: string;
	youtube_url?: string;
	soundcloud_url?: string;
	production_company?: string;
	presents_name?: string | null;
	is_collaborative?: boolean;
	collaboration_format?: "×" | "x" | "B2B" | "+" | "&" | "vs." | ",";
	collaboration_alias?: string | null;
	show_artists?: Array<{
		id: string;
		position: number;
		time_start?: string;
		time_end?: string;
		is_headliner: boolean;
		role: "headliner" | "collaborator" | "supporting_artist";
		artist: {
			id: string;
			name: string;
			image_url?: string | null;
			[key: string]: unknown;
		};
	}>;
}

export interface ShowGridProps {
	shows: Show[];
	facepileData?: ShowFacepileData;
	isFacepileLoading?: boolean;
	showDividers?: boolean;
}

/**
 * Group shows by date and month for divider rendering
 */
function groupShowsByDate(shows: Show[]) {
	const groups: Array<{
		type: "month" | "day";
		date: Date;
		show?: Show;
	}> = [];

	let lastMonth = "";
	let lastDate = "";

	for (const show of shows) {
		// Parse date without timezone conversion - treat as local date
		// Split "2025-10-14" and create Date in local timezone
		const parts = show.date.split("-");
		const year = Number(parts[0]);
		const month = Number(parts[1]);
		const day = Number(parts[2]);
		const showDate = new Date(year, month - 1, day);
		const monthKey = `${showDate.getFullYear()}-${showDate.getMonth()}`;
		const dateKey = show.date;

		// Add month divider if month changed
		if (monthKey !== lastMonth) {
			groups.push({ type: "month", date: showDate });
			lastMonth = monthKey;
		}

		// Add day divider if date changed
		if (dateKey !== lastDate) {
			groups.push({ type: "day", date: showDate });
		}

		// Add the show
		groups.push({ type: "day", date: showDate, show });
		lastDate = dateKey;
	}

	return groups;
}

export const ShowGrid = memo(function ShowGrid({
	shows,
	facepileData = {},
	isFacepileLoading = false,
	showDividers = true,
}: ShowGridProps) {
	const { priorityCount } = useResponsiveLimit();

	// Group shows with dividers
	const groupedItems = useMemo(() => {
		if (!showDividers) return shows.map((show) => ({ show }));
		return groupShowsByDate(shows);
	}, [shows, showDividers]);

	let showIndex = 0; // Track actual show index for priority

	return (
		<div className="space-y-3">
			{groupedItems.map((item) => {
				// Render divider
				if ("type" in item && !item.show) {
					return (
						<ShowDateDivider
							key={`divider-${item.type}-${item.date.toISOString()}`}
							date={item.date}
							type={item.type}
						/>
					);
				}

				// Render show
				const show = "show" in item ? item.show : item;
				if (!show || !("id" in show)) return null;

				const interactions = facepileData?.[show.id] || [];
				const isHighPriority = showIndex < priorityCount;
				showIndex++;

				return (
					<ShowDiscoveryCard
						key={show.id}
						show={show}
						interactions={interactions}
						isFacepileLoading={isFacepileLoading}
						priority={isHighPriority}
						fetchPriority={isHighPriority ? "high" : "low"}
						loading={isHighPriority ? "eager" : "lazy"}
					/>
				);
			})}
		</div>
	);
});
